import React, { useContext, useEffect } from 'react';
import { IconFolderUser, IconChartSegment, IconEmail, IconCheckCircle } from '@sm/wds-icons';

import { Box } from '@wds/box';
import { Grid } from '@wds/grid';
import { List } from '@wds/list';
import { Typography } from '@wds/typography';

import { StaticContext } from '@sm/webassets';

import DeveloperWebBasePage from '../DeveloperWebBasePage';

import waitForUsabilla from '~app/helpers/waitForUsabilla';

import AppTable from '../../components/AppTable';

import './build-a-private-app.scss';

const renderAppChecklist = () => {
  return (
    <List noRules transparent dense>
      <List.Item>
        <Typography variant="body">
          Here&apos;s a sample guide for creating an end-to-end customer feedback app using the SurveyMonkey API:{' '}
        </Typography>
      </List.Item>
      <List.Item>
        <Typography variant="body">
          After authorizing into a SurveyMonkey account, the user chooses from a list of existing surveys, a
          SurveyMonkey template, or creates a new survey.{' '}
        </Typography>
      </List.Item>
      <List.Item>
        <Typography variant="body">
          If the survey results need to correspond to an existing object (e.g. product, customer, support case), the
          developer can automatically add a custom variable to the survey.{' '}
        </Typography>
      </List.Item>
      <List.Item>
        <Typography variant="body">
          An app can then grab a weblink and send a survey through either their own email tool, or use
          SurveyMonkey&apos;s email system.
        </Typography>
      </List.Item>
      <List.Item>
        <Typography variant="body">
          Response data is stored in SurveyMonkey, and automatically returned via the API to the partner in real time
          (using webhooks).
        </Typography>
      </List.Item>
    </List>
  );
};

const renderAPIChecklist = () => {
  return (
    <List noRules transparent dense>
      <List.Item
        addOnLeft={
          <span className="green-icon">
            <IconCheckCircle />
          </span>
        }
      >
        <Typography variant="body">
          <strong>Use webhooks instead of polling.</strong> Besides providing added stability, webhooks reduce the
          number of calls needed to be made on the client side by only calling the API when there&apos;s new activity
          (rather than on a regular basis).
        </Typography>
      </List.Item>
      <List.Item
        addOnLeft={
          <span className="green-icon">
            <IconCheckCircle />
          </span>
        }
      >
        <Typography variant="body">
          <strong>Caching on the client side.</strong> We see lots of apps continually requesting details for the same
          resource from the API. If the resource isn&apos;t going to change, cache it, and use webhooks to signal a
          refresh, if available.
        </Typography>
      </List.Item>
      <List.Item
        addOnLeft={
          <span className="green-icon">
            <IconCheckCircle />
          </span>
        }
      >
        <Typography variant="body">
          <strong>Queue up your changes.</strong> Bundle incremental updates to resources together, if they don&apos;t
          need to be sent over immediately.
        </Typography>
      </List.Item>
      <List.Item
        addOnLeft={
          <span className="green-icon">
            <IconCheckCircle />
          </span>
        }
      >
        <Typography variant="body">
          <strong>Use the Bulk API endpoints.</strong> Where applicable and available (survey details, responses,
          recipients, contacts), try making bulk calls.
        </Typography>
      </List.Item>
    </List>
  );
};

const BuildAPrivateApp = () => {
  const {
    environment: { subdomain },
  } = useContext(StaticContext);

  useEffect(() => {
    waitForUsabilla(() => {
      window.usabilla_live('show');
    });
  }, []);

  // For testing only, when this goes live to the devekoper subdomain this will not be required
  const prependUrl = subdomain !== 'developer' && subdomain !== 'developer.eu' ? '/developer' : '';

  return (
    <div className="container-build-a-private-app" data-testid="BuildAPrivateApp__Developer">
      <DeveloperWebBasePage id="Developer - BuildAPrivateApp" title="SurveyMonkey API Developer Portal">
        <div className="build-a-private-app-page">
          <Box mb={6}>
            <Box my={8}>
              <Box mb={4}>
                <Typography variant="pageTitle" component="h1" align="center">
                  Creating a Private App
                </Typography>
              </Box>
              <Box>
                <Typography variant="body" align="center">
                  Apps built for private use can take many forms.
                  <br />
                  Some examples may include:
                </Typography>
              </Box>
            </Box>
            <Box mb={6}>
              <Grid>
                <Grid.Item>
                  <div className="private-app-icon">
                    <IconFolderUser />
                  </div>
                  <Box>
                    <Typography variant="body" align="center">
                      An organization purchases multiple seats for employees and creates an app for internal use
                    </Typography>
                  </Box>
                </Grid.Item>
                <Grid.Item>
                  <div className="private-app-icon">
                    <IconChartSegment />
                  </div>
                  <Box>
                    <Typography variant="body" align="center">
                      A developer automates sending surveys and/or exporting survey data for an SM account or Team
                    </Typography>
                  </Box>
                </Grid.Item>
                <Grid.Item>
                  <div className="private-app-icon">
                    <IconEmail />
                  </div>
                  <Box>
                    <Typography variant="body" align="center">
                      A team creates functionality to send thank you emails when their survey is completed
                    </Typography>
                  </Box>
                </Grid.Item>
              </Grid>
            </Box>

            <Box mb={6}>
              <Box mb={6}>
                <Typography variant="body" align="center">
                  Private apps have different guidelines than apps publicly available in the App Directory. Here are a
                  few key differences:
                </Typography>
              </Box>

              <AppTable privateTable />

              <Box mb={2} mt={4}>
                <Typography variant="body">
                  Think your app is better suited to be Public? Check out{' '}
                  <a href={`${prependUrl}/build-a-public-app/`}>Creating a Public App</a>.
                </Typography>
              </Box>
              <Box>
                <Typography variant="body">
                  *Please see our <a href="/tou/">API Developer Terms</a> for more guidelines when building with our
                  API.
                </Typography>
              </Box>

              <Box mb={2} mt={6}>
                <Typography variant="cardTitle" component="h3">
                  Building a Private App
                </Typography>
              </Box>

              {renderAppChecklist()}

              <Box mb={2} mt={6}>
                <Typography variant="cardTitle" component="h3">
                  Tips for reducing your API call usage
                </Typography>
              </Box>

              {renderAPIChecklist()}
            </Box>
          </Box>
        </div>
      </DeveloperWebBasePage>
    </div>
  );
};

export default BuildAPrivateApp;
